import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi } from '../api';

/**
 * My Care — every booking, order and encounter across the services, in one
 * list, newest first.
 *
 * Until now each kind of care answered only for itself: an appointment on the
 * Appointments page, an order on Pharmacy, a scan on Diagnostics, an
 * admission on Hospital Record. Nothing answered "what is happening with my
 * care" in one place, so a patient with a scan tomorrow and a delivery today
 * had to go looking in four screens to find out.
 *
 * Each source is fetched on its own and a source that is down is *named*
 * rather than silently dropped — a short list that looks complete is the one
 * failure mode this screen cannot have.
 *
 * What can be acted on here is only what the server actually supports.
 * Appointments and lab bookings have a cancel route and an ambulance request
 * carries its own `cancellable` flag; an imaging order or a pharmacy order is
 * cancelled by the centre or the shop that holds it, so this offers no button
 * that would only come back with a 404.
 */

type Kind = 'appointment' | 'lab' | 'imaging' | 'pharmacy' | 'hospital' | 'ambulance' | 'insurance';

interface CareItem {
  key: string;
  kind: Kind;
  sourceId: string;
  title: string;
  subtitle: string;
  /** The date this is sorted and shown by: when it happens, else when it was made. */
  at: string | null;
  atLabel: string;
  status: string;
  paymentStatus?: string;
  amount?: number | null;
  place?: string;
  /** Where the module that owns this item lives. */
  to: string;
  cancellable: boolean;
}

const KINDS: { kind: Kind | null; label: string; icon: string }[] = [
  { kind: null, label: 'Everything', icon: '🗂' },
  { kind: 'appointment', label: 'Doctor', icon: '🩺' },
  { kind: 'lab', label: 'Lab', icon: '🧪' },
  { kind: 'imaging', label: 'Imaging', icon: '🩻' },
  { kind: 'pharmacy', label: 'Pharmacy', icon: '💊' },
  { kind: 'hospital', label: 'Hospital', icon: '🏨' },
  { kind: 'ambulance', label: 'Ambulance', icon: '🚑' },
  { kind: 'insurance', label: 'Insurance', icon: '🛡' },
];

const KIND_LABEL: Record<Kind, string> = {
  appointment: 'Doctor', lab: 'Lab', imaging: 'Imaging', pharmacy: 'Pharmacy',
  hospital: 'Hospital', ambulance: 'Ambulance', insurance: 'Insurance',
};

const DONE = new Set(['COMPLETED', 'DELIVERED', 'DISCHARGED', 'CLOSED', 'FULFILLED', 'REPORTED', 'EXPIRED', 'PAID']);
const OFF = new Set(['CANCELLED', 'CANCELED', 'REJECTED', 'NO_SHOW', 'MISSED', 'FAILED', 'LAPSED']);

type Bucket = 'live' | 'done' | 'off';

function bucketOf(status: string): Bucket {
  const s = (status || '').toUpperCase();
  if (OFF.has(s)) return 'off';
  if (DONE.has(s)) return 'done';
  return 'live';
}

const BUCKETS: { value: Bucket | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'live', label: 'In progress' },
  { value: 'done', label: 'Finished' },
  { value: 'off', label: 'Cancelled' },
];

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
  padding: 14, boxShadow: T.shadow, fontFamily: T.font,
};

function pretty(s?: string | null): string {
  return (s || '').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

function fmt(v?: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function money(n?: number | null): string {
  return typeof n === 'number' && n > 0 ? `₹${n.toLocaleString('en-IN')}` : '';
}

export default function MyCare() {
  const navigate = useNavigate();
  const [items, setItems] = useState<CareItem[]>([]);
  const [missing, setMissing] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState<Kind | null>(null);
  const [bucket, setBucket] = useState<Bucket | null>(null);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const down: string[] = [];
    const rows: CareItem[] = [];

    const pull = async <A,>(label: string, fn: () => Promise<A[] | null>, map: (row: A) => CareItem) => {
      try {
        for (const r of (await fn()) ?? []) rows.push(map(r));
      } catch {
        // One service being unreachable must not empty the whole list — but it
        // must be said, or a short list reads as "nothing is happening".
        down.push(label);
      }
    };

    await Promise.all([
      pull('Appointments', () => patientApi.getAppointments(), (a: any) => ({
        key: `appointment:${a.id}`, kind: 'appointment', sourceId: a.id,
        title: a.doctorName || 'Doctor appointment',
        subtitle: [a.speciality, pretty(a.consultationType)].filter(Boolean).join(' · '),
        at: a.appointedAt ?? null, atLabel: 'Appointment',
        status: a.status || 'SCHEDULED',
        place: pretty(a.consultationType),
        to: '/patient/appointments',
        cancellable: ['SCHEDULED', 'CONFIRMED', 'PENDING'].includes((a.status || '').toUpperCase()),
      })),
      pull('Lab bookings', () => patientApi.lab.getAppointments() as Promise<any[]>, (b: any) => ({
        key: `lab:${b.id}`, kind: 'lab', sourceId: b.id,
        title: b.testName || (b.testCount > 0 ? `${b.testCount} tests` : 'Lab booking'),
        subtitle: [b.appointmentNumber, b.providerName || b.labName].filter(Boolean).join(' · '),
        at: b.scheduledAt ?? b.bookedAt ?? null, atLabel: b.scheduledAt ? 'Sample due' : 'Booked',
        status: b.status || 'BOOKED',
        place: pretty(b.collectionType || b.collectionMode),
        to: `/patient/lab-appointments/${b.id}`,
        cancellable: ['BOOKED', 'CONFIRMED', 'SCHEDULED', 'PENDING'].includes((b.status || '').toUpperCase()),
      })),
      pull('Imaging orders', () => patientApi.getImagingOrders(), (o) => ({
        key: `imaging:${o.id}`, kind: 'imaging', sourceId: o.id,
        title: o.testName || o.modality || 'Scan',
        subtitle: [o.orderNumber, o.bodyPart, o.facility].filter(Boolean).join(' · '),
        at: o.scheduledFor ?? o.orderedAt ?? null, atLabel: o.scheduledFor ? 'Scan due' : 'Ordered',
        status: o.status || 'ORDERED',
        place: o.facility ?? undefined,
        to: '/patient/diagnostics',
        // No cancel route exists for an imaging order; the centre cancels it.
        cancellable: false,
      })),
      pull('Pharmacy orders', () => patientApi.pharmacyModule.orders(), (o) => ({
        key: `pharmacy:${o.orderNumber || o.id}`, kind: 'pharmacy', sourceId: o.orderNumber || o.id,
        title: o.pharmacy?.name || 'Pharmacy order',
        subtitle: [o.orderNumber, o.items?.length ? `${o.items.length} item${o.items.length === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · '),
        at: o.placedAt ?? null, atLabel: 'Placed',
        status: o.status || 'PLACED',
        paymentStatus: o.paymentStatus,
        amount: o.totalAmount,
        place: pretty(o.fulfillmentType),
        to: '/patient/pharmacy',
        cancellable: false,
      })),
      pull('Hospital record', () => patientApi.hospital.encounters(), (e) => ({
        key: `hospital:${e.encounterId}`, kind: 'hospital', sourceId: e.encounterId,
        title: e.hospitalName || 'Hospital',
        subtitle: [e.kind === 'IPD' ? 'Admission' : 'OPD visit', e.departmentName, e.attendingDoctorName].filter(Boolean).join(' · '),
        at: e.admittedAt ?? e.scheduledAt ?? e.lastEventAt ?? null,
        atLabel: e.admittedAt ? 'Admitted' : 'Visit',
        status: e.status || '',
        place: e.ward || e.departmentName || undefined,
        to: '/patient/hospital',
        cancellable: false,
      })),
      pull('Ambulance', () => patientApi.ambulance.trips(), (t) => ({
        key: `ambulance:${t.id}`, kind: 'ambulance', sourceId: t.id,
        title: pretty(t.ambulanceType || t.requestType) || 'Ambulance',
        subtitle: [t.bookingNumber, t.assignment?.vehicleNumber].filter(Boolean).join(' · '),
        at: t.createdAt ?? null, atLabel: 'Requested',
        status: t.status || '',
        amount: t.fare?.total ?? null,
        place: t.pickup?.address ?? undefined,
        to: '/patient/ambulance',
        // The server says whether this one may still be called off.
        cancellable: !!t.cancellable,
      })),
      pull('Insurance', () => patientApi.getInsurancePolicies(), (p) => ({
        key: `insurance:${p.id}`, kind: 'insurance', sourceId: p.id,
        title: p.providerName || 'Insurance policy',
        subtitle: [p.planName, p.policyNumber].filter(Boolean).join(' · '),
        at: p.validTo || null, atLabel: 'Cover ends',
        status: p.validTo && new Date(p.validTo).getTime() < Date.now() ? 'EXPIRED' : (p.status || 'ACTIVE'),
        amount: p.coverageAmount,
        to: '/patient/insurance',
        cancellable: false,
      })),
    ]);

    rows.sort((a, b) => {
      const at = a.at ? new Date(a.at).getTime() : NaN;
      const bt = b.at ? new Date(b.at).getTime() : NaN;
      if (Number.isNaN(at) && Number.isNaN(bt)) return 0;
      if (Number.isNaN(at)) return 1;
      if (Number.isNaN(bt)) return -1;
      return bt - at;
    });

    setItems(rows);
    setMissing(down);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const cancel = async (it: CareItem) => {
    setBusy(it.key); setError(''); setNotice('');
    try {
      if (it.kind === 'appointment') await patientApi.cancelAppointment(it.sourceId);
      else if (it.kind === 'lab') await patientApi.lab.cancelAppointment(it.sourceId);
      else if (it.kind === 'ambulance') await patientApi.ambulance.cancel(it.sourceId);
      setNotice(`${KIND_LABEL[it.kind]} cancelled.`);
      await load();
    } catch (e: any) {
      setError(e?.body?.message || 'That could not be cancelled — the provider may already have started it.');
    } finally {
      setBusy('');
    }
  };

  const shown = useMemo(
    () => items.filter((i) => (!kind || i.kind === kind) && (!bucket || bucketOf(i.status) === bucket)),
    [items, kind, bucket],
  );

  const live = useMemo(() => items.filter((i) => bucketOf(i.status) === 'live').length, [items]);

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h1 style={{ ...GOV_TYPE.pageTitle }}>My care</h1>
        <p style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, fontFamily: T.font, lineHeight: 1.5 }}>
          Every appointment, booking, order and hospital visit on your record — newest first.
          {!loading && items.length > 0 && ` ${live} still in progress.`}
        </p>
      </div>

      {missing.length > 0 && (
        <div style={{ ...card, borderColor: `${T.warning}55`, padding: 12, fontSize: 11.5, color: T.textSec, lineHeight: 1.5 }}>
          {missing.join(' and ')} could not be reached, so nothing from {missing.length === 1 ? 'it' : 'them'} is in
          this list. What you see below is not everything.
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {KINDS.map((k) => {
          const on = k.kind === kind;
          const n = k.kind ? items.filter((i) => i.kind === k.kind).length : items.length;
          return (
            <button
              key={k.label}
              onClick={() => setKind(k.kind)}
              style={{
                padding: '8px 13px', borderRadius: 20, cursor: 'pointer',
                fontSize: 11, fontWeight: 700, fontFamily: T.font,
                color: on ? T.white : T.textSec,
                background: on ? T.teal : T.white,
                border: `1px solid ${on ? T.teal : T.border}`,
              }}
            >
              {k.icon} {k.label}{!loading && n > 0 ? ` (${n})` : ''}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {BUCKETS.map((b) => {
          const on = b.value === bucket;
          return (
            <button
              key={b.label}
              onClick={() => setBucket(b.value)}
              style={{
                padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                fontSize: 10.5, fontWeight: 700, fontFamily: T.font,
                color: on ? T.text : T.textTer,
                background: on ? T.ice : 'transparent',
                border: `1px solid ${on ? T.border : 'transparent'}`,
              }}
            >
              {b.label}
            </button>
          );
        })}
      </div>

      {error && <div style={{ ...card, borderColor: `${T.danger}55`, padding: 12, fontSize: 12, color: T.danger }}>{error}</div>}
      {notice && <div style={{ ...card, borderColor: `${T.teal}55`, padding: 12, fontSize: 12, color: T.teal }}>{notice}</div>}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>Gathering your care across the services…</div>
      ) : shown.length === 0 ? (
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🗂</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
            {items.length === 0 ? 'Nothing on your record yet' : 'Nothing matches this filter'}
          </div>
          <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6, lineHeight: 1.6 }}>
            {items.length === 0
              ? 'Anything you book, order or are treated for will appear here.'
              : 'Try another service or status.'}
          </div>
        </div>
      ) : (
        shown.map((it) => {
          const b = bucketOf(it.status);
          const badge = b === 'off' ? T.danger : (b === 'done' ? T.textSec : T.teal);
          return (
            <div key={it.key} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textTer, letterSpacing: 0.4 }}>
                    {KIND_LABEL[it.kind].toUpperCase()}
                  </div>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text, marginTop: 3 }}>{it.title}</div>
                  {it.subtitle && (
                    <div style={{ fontSize: 11, color: T.textSec, marginTop: 3 }}>{it.subtitle}</div>
                  )}
                  <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 5 }}>
                    {[
                      it.at ? `${it.atLabel} ${fmt(it.at)}` : null,
                      it.place || null,
                      money(it.amount),
                      it.paymentStatus ? pretty(it.paymentStatus) : null,
                    ].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <span style={{
                  fontSize: 9.5, fontWeight: 700, whiteSpace: 'nowrap', borderRadius: 8,
                  padding: '3px 8px', color: T.white, background: badge,
                }}>
                  {pretty(it.status) || 'Recorded'}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button
                  onClick={() => navigate(it.to)}
                  style={{
                    padding: '8px 14px', borderRadius: 10, border: `1px solid ${T.border}`,
                    background: T.white, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                    color: T.teal, fontFamily: T.font,
                  }}
                >
                  Open in {KIND_LABEL[it.kind]}
                </button>
                {it.cancellable && b === 'live' && (
                  <button
                    onClick={() => cancel(it)}
                    disabled={busy === it.key}
                    style={{
                      padding: '8px 14px', borderRadius: 10, border: `1px solid ${T.danger}40`,
                      background: T.white, cursor: busy === it.key ? 'default' : 'pointer',
                      fontSize: 11, fontWeight: 700, color: T.danger, fontFamily: T.font,
                    }}
                  >
                    {busy === it.key ? 'Cancelling…' : 'Cancel'}
                  </button>
                )}
              </div>

              {/* Where the patient must go instead, rather than a button that
                  would only be refused by the server. */}
              {!it.cancellable && b === 'live' && (it.kind === 'pharmacy' || it.kind === 'imaging') && (
                <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 8, lineHeight: 1.5 }}>
                  {it.kind === 'pharmacy'
                    ? 'A pharmacy order is cancelled by the shop — message them from Care Chat, or call the number on the order.'
                    : 'An imaging order is cancelled by the centre that scheduled it.'}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
